import DOMPurify from 'dompurify';
import { z } from 'zod';

/**
 * Sanitization utilities for input validation and XSS prevention
 */

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'code', 'pre'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
  });
}

/**
 * Strip all HTML tags from a string
 */
export function stripHtml(input: string): string {
  return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
}

/**
 * Sanitize text input - trims and removes dangerous characters
 */
export function sanitizeText(input: string): string {
  if (!input) return '';
  return input
    .trim()
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // Remove control characters
    .slice(0, 10000); // Limit length
}

/**
 * Sanitize a URL - validates and returns safe URL
 */
export function sanitizeUrl(input: string): string | null {
  if (!input) return null;
  
  try {
    const url = new URL(input.trim());
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(url.protocol)) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Sanitize email address
 */
export function sanitizeEmail(input: string): string {
  return input.trim().toLowerCase().slice(0, 255);
}

/**
 * Escape special characters for use in SQL LIKE queries
 */
export function escapeForLike(input: string): string {
  return input
    .replace(/\\/g, '\\\\')
    .replace(/%/g, '\\%')
    .replace(/_/g, '\\_')
    .replace(/'/g, "''");
}

/**
 * Encode string for safe URL parameter usage
 */
export function encodeUrlParam(input: string): string {
  return encodeURIComponent(sanitizeText(input));
}

/**
 * Common Zod schemas for validated input
 */
export const validationSchemas = {
  // Safe text input (trimmed, max length)
  safeText: (maxLength = 1000) => 
    z.string()
      .trim()
      .max(maxLength, `Must be less than ${maxLength} characters`)
      .transform(sanitizeText),

  // Safe multiline text
  safeTextarea: (minLength = 0, maxLength = 5000) =>
    z.string()
      .trim()
      .min(minLength, `Must be at least ${minLength} characters`)
      .max(maxLength, `Must be less than ${maxLength} characters`)
      .transform(sanitizeText),

  // Email validation
  email: () =>
    z.string()
      .trim()
      .email('Invalid email address')
      .max(255, 'Email must be less than 255 characters')
      .transform(sanitizeEmail),

  // URL validation
  url: () =>
    z.string()
      .trim()
      .url('Invalid URL')
      .refine((url) => {
        try {
          const parsed = new URL(url);
          return ['http:', 'https:'].includes(parsed.protocol);
        } catch {
          return false;
        }
      }, 'URL must use http or https protocol'),

  // Optional URL
  optionalUrl: () =>
    z.string()
      .trim()
      .url('Invalid URL')
      .refine((url) => {
        try {
          const parsed = new URL(url);
          return ['http:', 'https:'].includes(parsed.protocol);
        } catch {
          return false;
        }
      }, 'URL must use http or https protocol')
      .optional()
      .or(z.literal('')),

  // Safe slug (alphanumeric + hyphens)
  slug: () =>
    z.string()
      .trim()
      .min(1, 'Slug is required')
      .max(100, 'Slug must be less than 100 characters')
      .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),

  // Safe username
  username: () =>
    z.string()
      .trim()
      .min(3, 'Username must be at least 3 characters')
      .max(30, 'Username must be less than 30 characters')
      .regex(/^[a-zA-Z0-9_-]+$/, 'Username can only contain letters, numbers, underscores, and hyphens'),

  // Password
  password: () =>
    z.string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password must be less than 128 characters'),

  // Safe rating (1-5)
  rating: () =>
    z.number()
      .min(1, 'Rating must be at least 1')
      .max(5, 'Rating must be at most 5'),

  // Price in cents (positive integer)
  price: () =>
    z.number()
      .int('Price must be a whole number')
      .min(0, 'Price cannot be negative')
      .max(999999999, 'Price is too large'),
};

/**
 * Rate limiting helper - checks if action should be throttled
 */
export function shouldThrottle(
  lastActionTime: number | null,
  cooldownMs: number
): boolean {
  if (!lastActionTime) return false;
  return Date.now() - lastActionTime < cooldownMs;
}

/**
 * Generate a safe display name from user input
 */
export function safeDisplayName(input: string, maxLength = 50): string {
  return sanitizeText(input)
    .replace(/[<>'"]/g, '')
    .slice(0, maxLength);
}

/** Quote a CSV cell and neutralize spreadsheet formula injection. */
export function csvCell(value: unknown): string {
  let s = String(value ?? '');
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
}
